import { useActiveWindowBreakpoint } from '@frameio/vapor';
import type { AssetResultMatch } from '../../../../../shared/types';
import {
  getOrderedMatches,
  mergeKeywordMatches,
} from '../../../../../shared/utils';

const MAX_MATCH_COUNT = {
  base: 1,
  desktop: 2,
};

type FeaturedMatchesResult = {
  featuredMatches: AssetResultMatch[];
  additionalMatches: AssetResultMatch[];
};

/**
 * Splits matches into featured and additional groups based on max count
 */
const splitMatchesByMaxCount = (
  matches: AssetResultMatch[],
  maxMatchCount: number
): [AssetResultMatch[], AssetResultMatch[]] => {
  if (!matches || matches.length === 0) return [[], []];

  return [matches.slice(0, maxMatchCount), matches.slice(maxMatchCount)];
};

/**
 * @hack
 * TranscriptionMatch contains multiple snippets, which need to be flattened
 * into individual TranscriptionResultMatch items for proper UI rendering.
 * Each snippet is decorated with index and totalCount for display logic.
 * This needs to be reworked.
 */
const flattenTranscriptionMatches = (
  matches: AssetResultMatch[]
): AssetResultMatch[] => {
  return matches.flatMap((match) => {
    if (match.__typename === 'TranscriptionMatch') {
      const transcriptionMatch = match.matches?.nodes?.[0];

      return (
        transcriptionMatch?.snippets?.map((snippet, index) => ({
          __typename: 'TranscriptionResultMatch' as const,
          id: transcriptionMatch?.id ?? '',
          snippets: [snippet],
          terms: transcriptionMatch?.terms ?? [],
          index,
          totalCount: transcriptionMatch?.snippets?.length ?? 0,
        })) ?? []
      );
    }
    return match;
  });
};

/**
 * Determines the maximum number of featured matches to show based on match types.
 * Limits to 1 if any transcription or comment match exists.
 */
const getMaxMatchCount = (
  matches: AssetResultMatch[],
  breakpoint: string
): number => {
  const baseMaxCount =
    breakpoint !== 'desktop' ? MAX_MATCH_COUNT.base : MAX_MATCH_COUNT.desktop;

  const hasTranscriptionOrComment = matches.some(
    (match) =>
      match.__typename === 'TranscriptionResultMatch' ||
      match.__typename === 'CommentMatch'
  );

  // Limit to 1 featured match when any transcription or comment exists
  if (hasTranscriptionOrComment) {
    return MAX_MATCH_COUNT.base;
  }

  return baseMaxCount;
};

/**
 * Processes search result matches for display in the featured fields section
 * on the asset list item.
 *
 * @param matches - Raw array of search result matches
 * @returns Object containing featured matches and additional matches count
 */
export const useFeaturedMatches = (
  matches: AssetResultMatch[],
  featuredMatchCount?: number
): FeaturedMatchesResult => {
  const breakpoint = useActiveWindowBreakpoint();

  const flattenedMatches = flattenTranscriptionMatches(matches);
  const groupedMatches = mergeKeywordMatches(flattenedMatches); // consolidates KeywordsMatch into a single match with all terms in keywords array
  const orderedMatches = getOrderedMatches(groupedMatches);

  // Determine how many matches to feature based on breakpoint and match types
  const maxMatchCount = Number.isInteger(featuredMatchCount)
    ? (featuredMatchCount as number)
    : getMaxMatchCount(orderedMatches, breakpoint);

  // Split into featured and additional matches
  const [featuredMatches, additionalMatches] = splitMatchesByMaxCount(
    orderedMatches,
    maxMatchCount
  );

  return {
    featuredMatches,
    additionalMatches,
  };
};
